import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { User, Mail, Camera, Loader2, Lock, Eye, ShieldCheck, Save, FileText, CheckCircle2, Calendar, Upload } from "lucide-react";
import { api, formatApiError, uploadFile, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Footer from "@/components/Footer";
import { AdminVerifiedBadge } from "@/components/AdminVerifiedBadge";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const isHost = user?.role === "provider";
  const fileRef = useRef(null);

  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [avatar, setAvatar] = useState(user?.avatar_url || "");
  const [publicProfile, setPublicProfile] = useState(!!user?.public_profile);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const [docs, setDocs] = useState(user?.verification_documents || {});
  const [docBusy, setDocBusy] = useState("");

  const saveDocs = async (next) => {
    setDocs(next);
    try {
      await api.put("/profile", { verification_documents: next });
      await refreshUser();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const uploadDoc = async (key, file) => {
    if (!file) return;
    setDocBusy(key);
    try {
      const { url } = await uploadFile(file);
      await saveDocs({ ...docs, [key]: url });
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDocBusy("");
    }
  };

  const pickAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const { url } = await uploadFile(f);
      setAvatar(url);
      toast.success("Photo uploaded — remember to save changes");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put("/profile", { name, email, avatar_url: avatar, bio, public_profile: publicProfile });
      await refreshUser();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async () => {
    if (newPw.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (newPw !== confirmPw) { toast.error("New passwords don't match"); return; }
    setSavingPw(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      if (data.access_token) localStorage.setItem("carivisor_token", data.access_token);
      setCurPw(""); setNewPw(""); setConfirmPw("");
      toast.success("Password changed successfully");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingPw(false);
    }
  };

  const initial = (name || "?")[0]?.toUpperCase();
  const avatarSrc = avatar ? fileUrl(avatar) : "";
  const inputCls = "w-full bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-cyan-400/60 transition-colors";

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#060810]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <p className="text-xs font-mono-caption uppercase tracking-[0.3em] text-cyan-400 mb-2">Account</p>
          <h1 className="font-heading font-extrabold text-white text-4xl">Your profile</h1>
        </div>

        {/* Profile details */}
        <div className="glass rounded-2xl p-6 sm:p-8 mb-6" data-testid="profile-details-card">
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="flex flex-col items-center gap-3 shrink-0">
              <div className="relative w-28 h-28 rounded-full overflow-hidden bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                {avatarSrc
                  ? <img src={avatarSrc} alt="avatar" className="w-full h-full object-cover" data-testid="profile-avatar-img" />
                  : <span className="text-white font-heading font-extrabold text-4xl">{initial}</span>}
                {uploading && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><Loader2 className="w-6 h-6 text-white animate-spin" /></div>}
              </div>
              <input ref={fileRef} type="file" accept="image/*" onChange={pickAvatar} className="hidden" data-testid="avatar-file-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="change-photo-btn"
                className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full glass text-cyan-300 hover:text-white transition-colors">
                <Camera className="w-3.5 h-3.5" /> Change photo
              </button>
            </div>

            <div className="flex-1 space-y-4">
              <div>
                <label className="block text-xs font-mono-caption uppercase tracking-widest text-slate-400 mb-2">Full name</label>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-500 shrink-0" />
                  <input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-mono-caption uppercase tracking-widest text-slate-400 mb-2">Email address</label>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-500 shrink-0" />
                  <input data-testid="profile-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
                </div>
              </div>
              {isHost && (
                <div>
                  <label className="block text-xs font-mono-caption uppercase tracking-widest text-slate-400 mb-2">Host bio</label>
                  <textarea data-testid="profile-bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={3} placeholder="Tell travelers about yourself and your experiences…" className={inputCls + " resize-none"} />
                </div>
              )}
              <button onClick={saveProfile} disabled={savingProfile} data-testid="save-profile-btn"
                className="btn-neon rounded-xl px-6 py-3 font-semibold flex items-center gap-2 disabled:opacity-60">
                {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
              </button>
            </div>
          </div>
        </div>

        {/* Host public profile */}
        {isHost && (
          <div className="glass rounded-2xl p-6 sm:p-8 mb-6" data-testid="public-profile-card">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <p className="font-heading font-bold text-white text-lg flex items-center gap-2"><Eye className="w-4.5 h-4.5 text-fuchsia-400" /> Public profile</p>
                <p className="text-sm text-slate-400 mt-1">When on, your photo and bio appear to travelers on your tour pages.</p>
              </div>
              <button
                onClick={() => setPublicProfile((v) => !v)}
                data-testid="public-profile-toggle"
                role="switch"
                aria-checked={publicProfile}
                className={`relative w-14 h-7 rounded-full transition-colors shrink-0 ${publicProfile ? "bg-gradient-to-r from-cyan-500 to-blue-600" : "bg-[#232A4D]"}`}
              >
                <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${publicProfile ? "translate-x-7" : ""}`} />
              </button>
            </div>

            <p className="text-xs font-mono-caption uppercase tracking-widest text-slate-400 mb-3">Preview — how travelers see you</p>
            <div className="rounded-xl border border-[#1D2640] p-4 bg-[#0B0E1B]" data-testid="public-profile-preview">
              {publicProfile ? (
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0">
                    {avatarSrc ? <img src={avatarSrc} alt="" className="w-full h-full object-cover" /> : <span className="text-white font-heading font-bold text-lg">{initial}</span>}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-slate-400">Operated by</p>
                    <p className="font-heading font-semibold text-white flex items-center gap-1.5">{name}{user.admin_verified && <AdminVerifiedBadge showLabel={false} />}</p>
                    {bio && <p className="text-sm text-slate-300 mt-1 leading-relaxed">{bio}</p>}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-3">Your public profile is hidden. Only your business name shows on tours.</p>
              )}
            </div>
          </div>
        )}

        {/* Verification documents (hosts) */}
        {isHost && (
          <div className="glass rounded-2xl p-6 sm:p-8 mb-6" data-testid="verification-docs-card">
            <p className="font-heading font-bold text-white text-lg flex items-center gap-2 mb-1"><FileText className="w-4.5 h-4.5 text-orange-400" /> Verification documents</p>
            <p className="text-sm text-slate-400 mb-5">Please upload a high-quality photo or PDF of each document. An admin reviews these before approving your host account.</p>
            <div className="space-y-3">
              {[
                { key: "business_license", label: "Business License" },
                { key: "tourism_certificate", label: "Official Tourism Certificate", help: "Please upload a high-quality photo or PDF of your Valid Tourism Certificate." },
                { key: "government_id", label: "Government ID" },
              ].map((d) => {
                const uploaded = docs[d.key];
                return (
                  <div key={d.key} className="rounded-xl border border-[#1D2640] bg-[#0B0E1B] p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                          {uploaded && <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />}
                          {d.label}
                        </p>
                        {d.help && <p className="text-xs text-slate-500 mt-0.5">{d.help}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {uploaded && <a href={fileUrl(uploaded)} target="_blank" rel="noreferrer" data-testid={`view-doc-${d.key}`} className="text-xs px-3 py-1.5 rounded-lg glass text-cyan-300 hover:text-white">View</a>}
                        <label data-testid={`upload-doc-${d.key}`} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg btn-neon font-semibold cursor-pointer">
                          {docBusy === d.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                          {uploaded ? "Replace" : "Upload"}
                          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadDoc(d.key, e.target.files?.[0])} />
                        </label>
                      </div>
                    </div>
                    {d.key === "tourism_certificate" && (
                      <div className="mt-3 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-500 shrink-0" />
                        <label className="text-xs text-slate-400 whitespace-nowrap">Certificate expiry date</label>
                        <input data-testid="tourism-expiry-date" type="date" value={docs.tourism_certificate_expiry || ""}
                          onChange={(e) => saveDocs({ ...docs, tourism_certificate_expiry: e.target.value })}
                          className="bg-[#060810] border border-[#1D2640] rounded-lg px-3 py-1.5 text-sm text-white outline-none focus:border-cyan-400/60" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Change password */}
        <div className="glass rounded-2xl p-6 sm:p-8" data-testid="change-password-card">          <p className="font-heading font-bold text-white text-lg flex items-center gap-2 mb-4"><Lock className="w-4.5 h-4.5 text-cyan-400" /> Change password</p>
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
            <div className="sm:col-span-2">
              <label className="block text-xs font-mono-caption uppercase tracking-widest text-slate-400 mb-2">Current password</label>
              <input data-testid="current-password" type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} className={inputCls} placeholder="••••••••" />
            </div>
            <div>
              <label className="block text-xs font-mono-caption uppercase tracking-widest text-slate-400 mb-2">New password</label>
              <input data-testid="new-password" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className={inputCls} placeholder="At least 6 characters" />
            </div>
            <div>
              <label className="block text-xs font-mono-caption uppercase tracking-widest text-slate-400 mb-2">Confirm new password</label>
              <input data-testid="confirm-password" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className={inputCls} placeholder="Re-enter new password" />
            </div>
          </div>
          <button onClick={changePassword} disabled={savingPw || !curPw || !newPw} data-testid="change-password-btn"
            className="mt-5 btn-cool rounded-xl px-6 py-3 font-semibold flex items-center gap-2 disabled:opacity-60">
            {savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Update password
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
